import ConfirmModal from '@creator/ui/components/ConfirmModal/ConfirmModal';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import { getUserId } from '@src/model/user/helpers';
import { attachFlair, type Flair } from '@src/store/flairs/flairs';
import { usePost } from '@src/store/posts/hooks/usePost';
import type { FC } from 'react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlairsCard from '../FlairsCard/FlairsCard';

export interface EditFlairModalProps {
    className?: string;
    tokenName: string;
    postId: string;
}

const EditFlairModal: FC<EditFlairModalProps> = props => {
    const { postId, tokenName } = props;

    const { t } = useTranslation();
    const post = usePost(postId);
    const initialFlairs = post?.flairIds || [];

    const [isLoading, setIsLoading] = useState(false);
    const [selectedFlairs, setSelectedFlairs] = useState<string[]>(initialFlairs);

    const isFlairMandatory = post?.kind === 'story';
    const didFlairsChange = JSON.stringify([...selectedFlairs].sort()) !== JSON.stringify([...initialFlairs].sort());

    const { handleModal } = useContext<UseModal>(ModalContext);

    async function editFlair(): Promise<void> {
        if (!post) return;

        setIsLoading(true);
        await attachFlair({
            flairIds: selectedFlairs,
            postId,
            tokenName,
            userId: getUserId(),
        });
        setIsLoading(false);
        handleModal(null);
    }

    function onFlairClick(flairId: Flair['id']) {
        setSelectedFlairs(prev => {
            const isSelected = prev.includes(flairId);
            if (isFlairMandatory && prev.length === 1 && isSelected) {
                // Don't allow deselecting the last flair if mandatory
                return prev;
            }
            if (isSelected) {
                return prev.filter(id => id !== flairId);
            }
            return [...prev, flairId];
        });
    }

    function renderFlairSelection() {
        return (
            <div className="pt-8">
                <FlairsCard isFlairMandatory={isFlairMandatory} tokenName={tokenName} selectedFlairs={selectedFlairs} multiSelect={true} onFlairClick={onFlairClick} />
            </div>
        );
    }

    return (
        <ConfirmModal
            title={t('editFlair.title')}
            subtitle={t('editFlair.editFlairSubtitle')}
            onClick={() => handleModal(null)}
            okButtonProps={{
                isDisabled: !didFlairsChange,
                onClick: () => editFlair(),
                isLoading,
            }}
        >
            {renderFlairSelection()}
        </ConfirmModal>
    );
};

export default EditFlairModal;
